/**
 This module contains some reused garbage which does not use the store.
 * */
import { isNarrowScreen, isVeryNarrowScreen } from "./constants";
import type { UrlConfig } from "./types";

export const urlLocation = window.parent.location || window.location;

/**
 * Parse GET parameters into an object and return it.
 *
 * UPD: now supports parsing of a custom url if provided with a url argument.
 *
 * @returns UrlConfig
 */
export function getJsonFromUrl({ url = urlLocation } = {}): UrlConfig {
  // Supposedly we are in IFrame. In the top level window returns itself.
  const query = url.search.substr(1);
  const result = {};
  query.split("&").forEach((part) => {
    const item = part.split("=");
    result[item[0]] = decodeURIComponent(item[1]);
  });
  return result;
}

/**
 * Get ID of the YouTube video.
 * E.g. https://www.youtube.com/watch?v=_ImFiU0G_08 => _ImFiU0G_08
 * @param youTubeSrc
 * @returns {number|*}
 */
export const getYouTubeID = (youTubeSrc) => {
  const parsedUrl = getJsonFromUrl({ url: new URL(youTubeSrc) });
  return parsedUrl.v;
};

/**
 * Compose the image url for the YouTube preview.
 * @param youTubeSrc
 * @returns {string}
 */
export const getYouTubePreviewSrc = (youTubeSrc) => {
  return `http://i3.ytimg.com/vi/${getYouTubeID(youTubeSrc)}/maxresdefault.jpg`;
};

/**
 * Check user agent to determine the if we are on mobile.
 * Source - https://stackoverflow.com/a/11381730/1531116
 * Should gradually replace our lame isMobile constant.
 * @returns {boolean}
 */
export const mobileCheck = function () {
  let check = false;
  (function (a) {
    if (
      /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
        a
      ) ||
      /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
        a.substr(0, 4)
      )
    )
      check = true;
  })(navigator.userAgent || navigator.vendor || window.opera);
  return check;
};

export const isMobileUserAgent = mobileCheck();

export const getIsMobileLandscape = () => {
  return isMobileUserAgent && window.innerWidth > window.innerHeight;
};

/**
 * Some weird and complicated shit is happening here and below.
 * Shame on me.
 * Turns out we have 4 different classes that define the layout:
 * desktop, mobile, portrait and landscape. That's too much.
 * TODO: get rid of "mobile" probably, or somehow make it clear how it is used.
 */
export const addMobileClass = (classes = "") => {
  const mobileClass = getMobileClass();
  return `${classes}${mobileClass}`;
};

export const addLandscapeClass = (classes = "") => {
  const landscapeClass = getLandscapeClass();
  return `${classes}${landscapeClass}`;
};

export const getLandscapeClass = () => {
  return getIsMobileLandscape() ? " landscape " : "";
};

/**
 * Can't apply height > width logic to determine the landscape without
 * check for user agent.
 * TODO: there is a bug - for portrait iPad (simulated in chrome) we get "desktop" here.
 * @returns {string}
 */
export const getScreenLayout = () => {
  if (isMobileUserAgent) {
    if (getIsMobileLandscape()) {
      return "landscape";
    }
    return "portrait";
  }
  return "desktop";
};

export const getNarrowMidifier = () => {
  if (isNarrowScreen) {
    return "narrow";
  }
  if (isVeryNarrowScreen) {
    return "very-narrow";
  }
  return "";
};

export const getMobileClass = () => {
  return isMobileUserAgent ? " mobile" : "";
};

export const addMobileOrLandscapeClass = (classes = "") => {
  if (getIsMobileLandscape()) {
    return addLandscapeClass(classes);
  }
  if (isNarrowScreen) {
    return addMobileClass(classes);
  }
  return classes;
};

export const formatCurrency = (num) => {
  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
  });
  return formatter.format(num);
};

/**
 * Allows to substitute text patterns with custom variables.
 * @param text - message text.
 */
export const replaceCustomVariables = (text) => {
  if (!window.FamCustomVariables) {
    return text;
  }

  const items = window.FamCustomVariables;

  Object.entries(items).forEach((item) => {
    const [name, value] = item;
    text = text && text.replace(new RegExp(`{${name}}`, "g"), value);
  });
  return text;
};

// TODO: is it a demo lefover and should be deleted or is this actually used in producion?
window.replaceCustomVariables = replaceCustomVariables;

export const Show = ({ when = false, children }) => (when ? children : null);
export const urlConfig: UrlConfig = getJsonFromUrl();
export const noAnimation = urlConfig.noAnimation;
/**
 * For IE and selenium testing reasons.
 * https://stackoverflow.com/questions/25574963/ies-tolocalestring-has-strange-characters-in-results
 * May be I can just always use a custom built string here.
 * @param date
 * @returns {string}
 */
export const formatDate = (date) => {
  if (urlConfig.seleniumConfig) {
    return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
  }
  return date.toLocaleDateString("en-US"); // eslint-disable-line
};
